<template>
  <HeadTitle
    title="Vacaciones"
    description="Lista de las solicitudes de vacaciones y sus estados de aprobación"
  />
  <divider></divider>
  <Toolbar class="mb-4 md:w-11">
    <template #start>
      <div class="flex-column align-items-center">
        <div class="flex flex-row">
          <div class="vacation-summary">
            <p class="summary-item">
              <strong>{{ vacationBalance }}</strong> días disponibles.
            </p>
          </div>
          <InfoButton
            message="El saldo será actualizado una vez que las vacaciones sean procesadas en el sistema de nomina"
          />
        </div>
      </div>
    </template>
    <template #end>
      <Button
        label="Nueva solicitud"
        icon="pi pi-plus"
        severity="primary"
        class="mr-2 font-semibold text-sm"
        @click="openNewRequestDialog"
      />
    </template>
  </Toolbar>
  <DataTable
    v-model:filters="filters"
    v-model:expandedRows="expandedRows"
    :value="vacations"
    dataKey="id"
    @rowExpand="onRowExpand"
    tableStyle="min-width: 60rem"
    class="tabla mb-3"
    paginator
    :rows="5"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    filter-display="row"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    :globalFilterFields="[
      'observation',
      'quantity',
      'dateInit',
      'dateEnd',
      'createdAt',
    ]"
  >
    <template #header>
      <div class="flex justify-content-end">
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column expander style="width: 5rem" />
    <Column
      field="globalStatus"
      header="Estado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '3rem' }"
    >
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class=""
        ></Tag>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <Dropdown
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="statuses"
          placeholder="filtrar estado"
          class="p-column-filter flex align-items-center"
          style="min-width: 12rem; height: 3rem"
          :showClear="true"
        >
          <template #value="{ value }">
            <span class="text-sm text-600 font-medium">
              {{ value && formatRequestState(value) }}
            </span>
          </template>
          <template #option="slotProps">
            <Tag
              :value="formatRequestState(slotProps.option)"
              :severity="getStatusLabel(slotProps.option)"
            />
          </template>
        </Dropdown>
      </template>
    </Column>
    <Column header="Descripción" field="observation">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Cantidad" field="quantity" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column
      header="Fecha de inicio"
      filter-field="dateInit"
      :show-filter-menu="false"
      field="dateInit"
      sortable
    >
    </Column>
    <Column header="Fecha final" field="dateEnd" sortable></Column>
    <Column header="Aprobadores">
      <template #body="{ data }">
        {{ data.approvedCount }} / {{ data.approversCount }}
      </template>
    </Column>
    <Column field="createdAt" header="Fecha de creación"> </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <div class="actions">
          <ConfirmPopup></ConfirmPopup>
          <Button
            v-if="
              data.globalStatus === 'APPROVED' &&
              !['27', '28'].includes(company?.code || '')
            "
            v-tooltip.bottom="'Ver recibo'"
            class="action-btn view hover:text-blue-800 mr-2"
            severity="primary"
            icon="pi pi-print"
            @click="onVacationReceipt(data.id)"
          ></Button>
          <Button
            v-if="data.file"
            v-tooltip.bottom="'ver adjunto'"
            class="action-btn view hover:text-blue-800 mr-2"
            severity="secondary"
            icon="pi pi-paperclip"
            @click="downloadAttachment(data.file)"
          ></Button>
          <Button
            v-if="
              data.approvedCount === 0 &&
              data.globalStatus === 'PENDING' &&
              data.origin !== 'PAYROLLSYSTEM'
            "
            v-tooltip.bottom="'Eliminar solicitud'"
            class="action-btn view btn hover:text-blue-800"
            severity="danger"
            icon="pi pi-trash"
            @click="deleteConfirm($event, data['id'])"
          ></Button>
        </div>
      </template>
    </Column>
    <template #expansion="slotProps">
      <div class="p-3">
        <h5>Detalle de aprobadores</h5>
        <DataTable :value="slotProps.data.steps">
          <template
            #empty
            v-if="slotProps.data.origin === 'PAYROLLSYSTEM'"
            class="flex flex-justify-center"
          >
            Solicitud creada desde el sistema de nómina
          </template>
          <template #empty v-else class="flex flex-justify-center">
            <i class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
            cargando...
          </template>
          <Column field="approver.name" header="Nombre"></Column>
          <Column field="resolutionDate" header="Fecha de resolución">
            <template #body="{ data, field }">
              <span v-if="data[field]" v-local-date="data[field]"></span>
              <span class="text-600 font-medium" v-else>Sin definir</span>
            </template>
          </Column>
          <Column field="weight" header="Prioridad"></Column>
          <Column field="resolutionStatus" header="Estado">
            <template #body="slotProps">
              <Tag
                :value="formatRequestState(slotProps.data.resolutionStatus)"
                :severity="getStatusLabel(slotProps.data.resolutionStatus)"
              >
              </Tag>
            </template>
          </Column>
          <Column field="resolutionComment" header="Comentario"> </Column>
        </DataTable>
      </div>
    </template>
  </DataTable>
  <Dialog
    v-model:visible="vacationDialog"
    :style="{ width: '450px' }"
    header="Nueva solicitud de vacaciones"
    :modal="true"
    class="p-fluid"
  >
    <div
      class="field flex justify-content-left mb-3"
      v-if="allowPayedVacations"
    >
      <label for="dataInit" class="font-medium text-700 mr-2"
        >Vacaciones en dinero</label
      >
      <InputSwitch v-model="vacation.isPaid" class="h-2" />
    </div>
    <div class="field">
      <label for="dataInit" class="font-medium text-700"
        >Fecha inicial <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dataInit"
        v-model="vacation.dateInit"
        required="true"
        :class="{ 'p-invalid': submitted && !vacation.dateInit }"
        :disabledDays="getDisabledDays"
        :disabled-dates="getDisabledDates"
        date-format="dd/mm/yy"
        @blur="calculateDateEnd"
      >
        <template #date="{ date }">
          <div class="flex flex-column">
            <span>{{ date.day }}</span>
            <span :class="getBadge(date)"></span>
          </div>
        </template>
        <template #footer>
          <div class="ml-6 mt-3">
            <div class="flex align-items-center">
              <div class="mr-2 vacation-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones aprobadas</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 vacation-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones pendientes</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 license-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias aprobadas</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 license-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias pendientes</label
              >
            </div>
            <div class="flex align-items-center">
              <div class="mr-2 holiday"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Feriados</label
              >
            </div>
          </div>
        </template>
      </Calendar>
      <small class="p-error" v-if="submitted && !vacation.dateInit"
        >La fecha inicial es requerida.</small
      >
    </div>
    <div class="field">
      <label for="cantDays" class="font-medium text-700 flex flex-row">
        Cantidad de dias ({{ vacationBalance - pendingBalance }} disponibles)
        <span class="text-red-500">*</span>
        <InfoButton
          :message="`Saldo acumulado: ${vacationBalance} días. Solicitudes pendientes por aprobar: ${pendingBalance} días. Días disponibles para nuevas solicitudes: ${
            vacationBalance - pendingBalance
          } días.`"
        />
      </label>
      <InputNumber
        id="cantDays"
        v-model="vacation.quantityDays"
        :class="{ 'p-invalid': submitted && !vacation.quantityDays }"
        :max="
          !allowNegativePTOBalance
            ? vacationBalance - pendingBalance
            : undefined
        "
        @blur="calculateDateEnd"
      />
      <small class="p-error" v-if="submitted && !vacation.quantityDays"
        >Cantidad de dias requeridos.</small
      >
    </div>
    <div class="field">
      <label for="dateEnd" class="font-medium text-700"
        >Fecha final <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dateEnd"
        v-model="vacation.dateEnd"
        :class="{ 'p-invalid': submitted && !vacation.dateEnd }"
        disabled
        date-format="dd/mm/yy"
      />
      <small class="p-error" v-if="submitted && !vacation.dateEnd"
        >La fecha final es requerida.</small
      >
    </div>
    <div class="field">
      <label for="adjunto" class="font-medium text-700">Adjuntar archivo</label>
      <FileUpload
        mode="basic"
        class="font-light text-sm w-12 text-center"
        custom-upload
        @uploader="removeFile"
        @select="saveFile($event)"
      />
    </div>
    <div class="field">
      <label for="observation" class="font-medium text-700"
        >Observación <span class="text-red-500">*</span></label
      >
      <Textarea
        id="observation"
        v-model="vacation.observation"
        required="true"
        :class="{ 'p-invalid': submitted && !vacation.observation }"
      />
      <small class="p-error" v-if="submitted && !vacation.observation"
        >La observación es requerida.</small
      >
    </div>
    <template #footer>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        @click="hideDialog"
        severity="secondary"
        class="text-sm font-light mr-2"
      />
      <Button
        label="Solicitar"
        icon="pi pi-check"
        class="text-sm font-light"
        :loading="isCreatingRequest"
        @click="saveRequest"
        :disabled="!isValidToSave"
      />
    </template>
  </Dialog>
  <ConfirmDialog class="md:w-7 w-6" group="negativeBalanceCF"></ConfirmDialog>
</template>
<script setup lang="ts">
  import Toolbar from 'primevue/toolbar'
  import { useUserStore } from '@/store'
  import { storeToRefs } from 'pinia'
  import { FilterMatchMode } from 'primevue/api'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useConfirm } from 'primevue/useconfirm'
  import Tag from 'primevue/tag'
  import {
    GetPTOPreferences,
    createVacationRequest,
    deleteVacationById,
    getDateEnd,
    getDaysByEmployeeId,
    getVacationReceipt,
    getVacationsByEmployeeId,
  } from '../services/vacationService'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import Vacation from '../models/Vacation'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import CreateVacationInputDto from '../services/interfaces/CreateVacationInputDto'
  import { getApprovalFlowStepsByRequest } from '@/modules/approvers/services/ApprovalRequestService'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import { getEmployeeById } from '../services/EmployeeService'
  import { WorkingDay } from '../models/WokingDay'
  import { Holiday } from '../models/Holiday'
  import { getLicensesByEmployeeId } from '../services/LicenseService'
  import { formatDate } from '@/shared/utils/formatDate'
  import License from '../models/License'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import dayjs from 'dayjs'
  const { user, company } = storeToRefs(useUserStore())

  const confirm = useConfirm()
  const vacations = ref<Vacation[]>()
  const licenses = ref<License[]>()
  const statuses = ref(['PENDING', 'APPROVED', 'REJECTED'])
  const vacationDialog = ref(false)
  const submitted = ref(false)
  const notificationStore = useNotifications()
  const loadingStore = useLoading()
  const isCreatingRequest = ref(false)
  const vacationBalance = ref(0)
  const workinDays = ref<WorkingDay[]>([])
  const holidays = ref<Holiday[]>([])
  const vacationsPending = ref<Date[]>([])
  const vacationsApproved = ref<Date[]>([])
  const licensesApproved = ref<Date[]>([])
  const licensesPending = ref<Date[]>([])
  const isPaid = ref(false)
  const allowPayedVacations = ref(false)
  const allowNegativePTOBalance = ref(false)
  const vacationsEnjoyed = ref(0)

  let vacation = ref<CreateVacationInputDto>({
    dateInit: undefined,
    dateEnd: undefined,
    quantityDays: 0,
    origin: 'NATIVE',
    observation: '',
    userId: user.value?.id || '',
    file: null,
    daysDetail: [],
    isPaid: false,
  })

  onMounted(async () => {
    await refreshData()
  })

  const saveFile = (event: any) => {
    const file = event.files[0]
    vacation.value.file = file
  }

  const removeFile = () => {
    vacation.value.file = null
  }

  const refreshData = async () => {
    loadingStore.setLoadingState()

    const [employeeResponse, vacationResponse, licensesResponse] =
      await Promise.all([
        getEmployeeById(user.value?.employeeId || ''),
        getVacationsByEmployeeId(user.value?.employeeId || ''),
        getLicensesByEmployeeId(user.value?.employeeId || ''),
      ])

    if (!employeeResponse || !vacationResponse || !licensesResponse) {
      loadingStore.dismissLoadingState()
      return
    }

    vacationBalance.value = employeeResponse?.vacationBalance || 0

    vacations.value = vacationResponse?.data.map((v) => ({
      ...v,
      dateInit: formatDate(v.dateInit),
      dateEnd: formatDate(v.dateEnd),
      createdAt: dayjs(v.createdAt).format('DD/MM/YYYY HH:mm'),
    }))

    licenses.value = licensesResponse?.data.map((l) => ({
      ...l,
      dateInit: formatDate(l.dateInit),
      dateEnd: formatDate(l.dateEnd),
      createdAt: formatDate(l.createdAt),
    }))

    if (user.value?.employeeId) {
      GetPTOPreferences(user.value?.employeeId).then((response) => {
        allowPayedVacations.value = response.allowPaidVacations
        allowNegativePTOBalance.value = response.allowNegativePTOBalance
      })
    }

    vacationsEnjoyed.value = vacationResponse.data.reduce((acc, v) => {
      if (v.globalStatus === 'APPROVED') {
        return acc + v.quantity
      }
      return acc
    }, 0)

    loadingStore.dismissLoadingState()
  }

  const expandedRows = ref([])

  const onRowExpand = async (event: any) => {
    if (event.data.origin !== 'NATIVE') {
      return
    }

    const requests = await getApprovalFlowStepsByRequest({
      approvalFlowId: event.data.approvalFlowId,
      approvalFlowRequestId: event.data.approvalFlowRequestId,
      employeeId: user.value?.employeeId || '',
    })

    vacations.value?.forEach((vacation: any) => {
      if (vacation.id === event.data.id) {
        vacation.steps = requests
      }
    })
  }

  const downloadAttachment = async (fileKey: string) => {
    if (!fileKey) return

    if (fileKey.includes('http')) {
      window.open(fileKey, '_blank')
      return
    }

    const fileUrl = await getFileUrlPresigned(fileKey)
    window.open(fileUrl, '_blank')
  }

  const deleteConfirm = (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Estás  seguro de eliminar esta solicitud?',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger p-button-sm',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        const response = await deleteVacationById(id)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Solicitud de vacaciones eliminada exitosamente`,
          })
          await refreshData()
        }
      },
      reject: () => {},
    })
  }

  const formatRequestState = (state: string) => {
    switch (state) {
      case 'PENDING':
        return 'Pendiente'
      case 'REJECTED':
        return 'Rechazado'
      case 'APPROVED':
        return 'Aprobado'
      default:
        return 'Pendiente'
    }
  }

  const openNewRequestDialog = async () => {
    vacationDialog.value = true
    if (workinDays.value?.length === 0 && holidays.value?.length === 0) {
      await getDaysByEmployeeId(user.value?.employeeId || '').then(
        (response) => {
          workinDays.value = response.workingDays
          holidays.value = response.holidays
        }
      )
    }
  }

  const getBadge = (dateRaw: any) => {
    const date = new Date(dateRaw.year, dateRaw.month, dateRaw.day)

    if (
      vacationsPending.value.some(
        (pendingDate) => pendingDate.getTime() === date.getTime()
      )
    ) {
      return 'vacation-pending'
    }

    if (
      vacationsApproved.value.some(
        (approvedDate) => approvedDate.getTime() === date.getTime()
      )
    ) {
      return 'vacation-approved'
    }

    if (
      licensesPending.value.some(
        (pendingDate) => pendingDate.getTime() === date.getTime()
      )
    ) {
      return 'license-pending'
    }

    if (
      licensesApproved.value.some(
        (approvedDate) => approvedDate.getTime() === date.getTime()
      )
    ) {
      return 'license-approved'
    }

    const dateString = date.toISOString().split('T')[0]

    if (holidays.value?.find((h) => h.date.split('T')[0] === dateString)) {
      return 'holiday'
    }

    return ''
  }

  const createRequest = async () => {
    isCreatingRequest.value = true
    const response = await createVacationRequest(vacation.value)
    isCreatingRequest.value = false
    vacationDialog.value = false

    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Solicitud de vacaciones creada exitosamente`,
      })

      await refreshData()
    }

    hideDialog()
  }

  const pendingBalance = computed(() => {
    return (
      vacations.value
        ?.filter((v) => v.globalStatus === 'PENDING')
        .reduce((acc, v) => acc + v.quantity, 0) ?? vacationBalance.value
    )
  })

  const saveRequest = async () => {
    // check preferences fo negative balance
    const newBalance =
      vacationBalance.value -
      (pendingBalance.value + vacation.value.quantityDays)

    if (
      !allowNegativePTOBalance.value &&
      newBalance < 0 &&
      !vacation.value.isPaid
    ) {
      loadingStore.dismissLoadingState()
      notificationStore.addInfoNotification({
        detail: `400`,
        severity: 'warn',
        summary: `No se puede crear la solicitud de vacaciones, el saldo de días de vacaciones disponibles no es suficiente.`,
      })
      return
    } else if (
      allowNegativePTOBalance.value &&
      newBalance < 0 &&
      !vacation.value.isPaid
    ) {
      confirm.require({
        message: `Atención: esta solicitud excede tu saldo disponible de vacaciones. Saldo actual: ${vacationBalance.value} días, pendientes: ${pendingBalance.value} días, solicitados: ${vacation.value.quantityDays} días, saldo proyectado: ${newBalance} días. ¿Deseas continuar con la solicitud?`,
        header: 'Confirmación',
        group: 'negativeBalanceCF',
        icon: 'pi pi-exclamation-circle',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Aceptar',
        rejectLabel: 'Cancelar',
        accept: async () => await createRequest(),
        reject: () => {},
      })
    } else {
      await createRequest()
    }
  }

  const hideDialog = () => {
    vacation.value = {
      dateInit: undefined,
      dateEnd: undefined,
      quantityDays: 0,
      origin: 'NATIVE',
      observation: '',
      userId: user.value?.id || '',
      file: null,
      daysDetail: [],
      isPaid: false,
    }

    isPaid.value = false
    vacationDialog.value = false
  }

  const calculateDateEnd = async () => {
    setTimeout(async () => {
      if (
        vacation.value.quantityDays > 0 &&
        vacation.value.dateInit &&
        vacation.value.dateInit.getTime() > 0
      ) {
        loadingStore.setLoadingState()

        const response = await getDateEnd({
          days: vacation.value.quantityDays,
          startDate: vacation.value.dateInit.toISOString().split('T')[0],
        })

        vacation.value.dateEnd = new Date(response.endDate + 'T00:00:00')

        const datesNoAvailables = [
          ...vacationsPending.value,
          ...vacationsApproved.value,
          ...licensesPending.value,
          ...licensesApproved.value,
        ].map((date) => date.toISOString().split('T')[0])

        if (
          response.dayToDay.some((date) => datesNoAvailables.includes(date))
        ) {
          notificationStore.addSuccesNotification({
            detail: `400`,
            severity: 'warn',
            summary: `Hay fechas no disponibles en el rango de fechas seleccionado`,
          })

          vacation.value.dateEnd = undefined
          vacation.value.quantityDays = 0
        }

        vacation.value.daysDetail = response.dayToDay
        loadingStore.dismissLoadingState()
      }
    }, 300)
  }

  const onVacationReceipt = async (vacationId: string) => {
    loadingStore.setLoadingState()
    const vacationBuffer = await getVacationReceipt(vacationId)
    loadingStore.dismissLoadingState()
    const url = window.URL.createObjectURL(vacationBuffer)
    window.open(url, '_blank')
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'

      case 'PENDING':
        return 'warning'

      case 'REJECTED':
        return 'danger'

      default:
        return 'warning'
    }
  }

  const getDisabledDays = computed(() => {
    const allDays = [0, 1, 2, 3, 4, 5, 6]

    workinDays.value?.forEach((day) => {
      if (day.isoWeekDay === 7) {
        day.isoWeekDay = 0 // Cambiamos el valor de 7 a 0 (domingo) para que se muestre correctamente en el calendario
      }

      // Verificamos si day.isoWeekDay está definido y es un número entre 0 y 6
      if (
        typeof day.isoWeekDay === 'number' &&
        allDays.includes(day.isoWeekDay)
      ) {
        // Eliminamos el día de la lista de días disponibles
        const index = allDays.indexOf(day.isoWeekDay)
        if (index !== -1) {
          allDays.splice(index, 1)
        }
      }
    })

    return allDays
  })

  const getDisabledDates = computed(() => {
    const disabledDates: Date[] = []

    holidays.value?.forEach((holiday) => {
      disabledDates.push(new Date(holiday.date.split('T')[0] + 'T00:00:00'))
    })

    vacations.value
      ?.filter((v) => v.globalStatus === 'PENDING' && v.isPaid === false)
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          vacationsPending.value.push(new Date(d + 'T00:00:00'))
        })
      })

    vacations.value
      ?.filter((v) => v.globalStatus === 'APPROVED' && v.isPaid === false)
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          vacationsApproved.value.push(new Date(d + 'T00:00:00'))
        })
      })

    licenses.value
      ?.filter((v) => v.globalStatus === 'PENDING')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          licensesPending.value.push(new Date(d + 'T00:00:00'))
        })
      })

    licenses.value
      ?.filter((v) => v.globalStatus === 'APPROVED')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          licensesApproved.value.push(new Date(d + 'T00:00:00'))
        })
      })

    return disabledDates
  })

  const filters = ref({
    globalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const isValidToSave = computed(() => {
    return (
      vacation.value.dateInit &&
      vacation.value.dateEnd &&
      vacation.value.quantityDays > 0 &&
      vacation.value.observation
    )
  })
</script>
<style scoped>
  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vacation-approved {
    background-color: #4caf50;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #4caf50;
  }

  .vacation-pending {
    background-color: #ff9800;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #ff9800;
  }

  .license-approved {
    background-color: rgb(228, 57, 57);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(228, 57, 57);
  }

  .license-pending {
    background-color: rgb(240, 243, 56);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(240, 243, 56);
  }

  .holiday {
    background-color: rgb(56, 56, 243);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(56, 56, 243);
  }

  tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }
</style>
